import classNames from 'classnames'
import { forwardRef } from 'react'
import type { RefObject } from 'react'

import { isMobile } from 'shared/utils/deviceClassUtils'
import CmsCanvasView from 'views/components/organisms/CmsMobileMenu/CmsCanvasView'
import type {
  CMSMobileMenuProps,
  CmsVisibleStackElement,
} from 'views/components/organisms/CmsMobileMenu/types'
import OffCanvasView from 'views/components/organisms/OffCanvas/OffCanvasView'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

const CmsMobileMenu = forwardRef(function CmsMobileMenu(
  {
    closeMenuHandler,
    goBackHandler,
    goNextHandler,
    hidden,
    language,
    userData,
    visibleStack = [],
  }: CMSMobileMenuProps,
  ref
) {
  const { deviceClass } = usePublicConfig()
  const navClass = classNames('u-hidden@print o-OffCanvas', {
    'u-hidden@medium': !isMobile(deviceClass),
  })

  return (
    <nav
      id="CmsMobileMenu"
      className={navClass}
      aria-hidden={Boolean(hidden)}
      aria-labelledby="CmsMobileMenuHeader"
      data-clientside-hook="menu"
      ref={ref as RefObject<never>}
    >
      {/*
        Navigation Views are DOM elements that are stacked on on top of the other.
        The behaviour of mobile menu navigation is achieved by simple DOM manipulation
        You enter a sub-category that has children, then another Navigation View is rendered
        and added to the list of NavigationViews.
      */}
      {visibleStack.map((view: CmsVisibleStackElement, index) => {
        const commonProps = {
          key: `${view.id}-${index}`,
          data: view,
          closeMenuHandler,
          goNextHandler,
        }

        // we assume that the first value of the visibleStack array is the data coming from CMS endpoint, thus making it the 1st View (CMSView)
        const isCmsView = index === 0

        if (isCmsView) {
          // This is the first view of the menu, the top-level view
          const cmsViewProps = {
            ...commonProps,
            categoryData: {
              topCategories: view.topCategories,
              categories: view.categories,
              bottomCategories: view.bottomCategories,
            },
            language,
            userData,
          }

          return <CmsCanvasView {...cmsViewProps} />
        }

        // once we click a category, we go to a sub-level view, this can be used as base view
        const viewProps = {
          ...commonProps,
          goBackHandler,
        }

        return <OffCanvasView {...viewProps} />
      })}
    </nav>
  )
})

export default CmsMobileMenu
